import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "./SearchContext";
import { useAuth } from "../auth/AuthContext";
import { getDatasourceFields } from "../../api/flowApi";

// Static field aliases the parser already understands (see SearchContext.canonicalField).
const STATIC_FIELDS = [
  "ArchiveNo",
  "DocumentDate",
  "Id",
  "FileExtension",
  "OriginalFileName",
  "archive",
  "customer",
  "account",
  "client",
  "date",
  "id",
  "type",
  "extension",
  "filename",
  "name",
];

const HINTS = [
  // Sentence starters
  { insert: "bring documents for ", label: "bring documents for …" },
  { insert: "show all documents ", label: "show all documents …" },
  { insert: "find documents ", label: "find documents …" },
  { insert: "list documents ", label: "list documents …" },
  { insert: "give me documents ", label: "give me documents …" },
  { insert: "fetch documents ", label: "fetch documents …" },
  // Customer / identifier phrasing
  { insert: "for customer with ID ", label: "for customer with ID …" },
  { insert: "customer with ID ", label: "customer with ID …" },
  { insert: "client number ", label: "client number …" },
  { insert: "account no ", label: "account no …" },
  { insert: "archive no ", label: "archive no …" },
  // Date range scaffolds
  { insert: "before ", label: "before <date>" },
  { insert: "after ", label: "after <date>" },
  { insert: "since ", label: "since <date>" },
  { insert: "between ", label: "between X and Y" },
  { insert: "from ", label: "from <date>" },
  { insert: "from last ", label: "from last …" },
  { insert: "in ", label: "in <month / year / database>" },
  { insert: "during ", label: "during <month>" },
  // Common ranges
  { insert: "today", label: "today" },
  { insert: "yesterday", label: "yesterday" },
  { insert: "this week", label: "this week" },
  { insert: "this month", label: "this month" },
  { insert: "this year", label: "this year" },
  { insert: "last week", label: "last week" },
  { insert: "last month", label: "last month" },
  { insert: "last year", label: "last year" },
  { insert: "last 7 days", label: "last 7 days" },
  { insert: "last 30 days", label: "last 30 days" },
];

const MONTH_NAMES = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

// Module-level cache so we only fetch fields per database once per session.
const fieldsCache = new Map();

export default function QueryAutocomplete({
  className,
  placeholder,
  disabled,
  onKeyDown,
  inputRef: externalInputRef,
}) {
  const { t } = useTranslation();
  const { query, setQuery, dbs, history } = useSearch();
  const { token } = useAuth();
  const localInputRef = useRef(null);
  const inputRef = externalInputRef || localInputRef;
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [cursor, setCursor] = useState(0);

  const ensureFields = async () => {
    if (!token || !dbs?.length) return;
    const out = new Set();
    await Promise.all(
      dbs.map(async (d) => {
        const name = d?.dbName;
        if (!name) return;
        if (!fieldsCache.has(name)) {
          try {
            const arr = await getDatasourceFields(token, name);
            fieldsCache.set(name, Array.isArray(arr) ? arr : []);
          } catch {
            fieldsCache.set(name, []);
          }
        }
        for (const f of fieldsCache.get(name) || []) out.add(f);
      })
    );
    setDynamicFields(Array.from(out));
  };

  const { tokenStart, currentToken } = useMemo(() => {
    const text = query || "";
    const pos = Math.min(cursor ?? text.length, text.length);
    let s = pos;
    while (s > 0 && /[A-Za-z0-9_]/.test(text[s - 1])) s -= 1;
    return { tokenStart: s, currentToken: text.slice(s, pos) };
  }, [query, cursor]);

  const dateContext = useMemo(() => {
    const text = query || "";
    const pos = Math.min(cursor ?? text.length, text.length);
    const before = text.slice(0, Math.max(0, pos - currentToken.length));
    if (/(?:^|\s)(before|after|since|from|to|between)\s+$/i.test(before)) return true;
    const m = before.match(/(?:^|\s)([A-Za-z][\w]*)\s*(?:>=|<=|>|<|=|:|is|equals)\s*$/i);
    if (m) {
      const f = m[1].toLowerCase();
      if (/^(date|documentdate|datetime|created|createdat)$/.test(f)) return true;
    }
    return false;
  }, [query, cursor, currentToken]);

  const monthContext = useMemo(() => {
    const text = query || "";
    const pos = Math.min(cursor ?? text.length, text.length);
    const before = text.slice(0, Math.max(0, pos - currentToken.length));
    return /(?:^|\s)(last|this|in|from|during)\s+$/i.test(before);
  }, [query, cursor, currentToken]);

  // DB names are only meaningful as a "scope" suffix — i.e. right after `in `.
  // Suppress them when the query is blank or the cursor isn't in a scope slot.
  const dbContext = useMemo(() => {
    const text = query || "";
    const pos = Math.min(cursor ?? text.length, text.length);
    const before = text.slice(0, Math.max(0, pos - currentToken.length));
    return /(?:^|\s)in\s+$/i.test(before);
  }, [query, cursor, currentToken]);

  const suggestions = useMemo(() => {
    const tok = (currentToken || "").toLowerCase();
    const items = [];

    // When the cursor sits right after "last/this/in/from/during ", the most
    // useful next thing is a month name — surface those first.
    if (monthContext) {
      for (const m of MONTH_NAMES) {
        if (!tok || m.startsWith(tok)) {
          items.push({ kind: "hint", label: m, insert: m });
        }
      }
    }

    if (dbContext) {
      for (const d of dbs || []) {
        const name = d?.dbName;
        if (!name) continue;
        if (!tok || name.toLowerCase().startsWith(tok)) {
          items.push({ kind: "db", label: name, insert: name });
        }
      }
    }
    const allFields = Array.from(new Set([...dynamicFields, ...STATIC_FIELDS]));
    for (const f of allFields) {
      if (!tok || f.toLowerCase().startsWith(tok)) {
        items.push({ kind: "field", label: f, insert: `${f}:` });
      }
    }
    for (const h of HINTS) {
      const lbl = h.label.toLowerCase();
      const ins = h.insert.toLowerCase();
      if (!tok || lbl.startsWith(tok) || ins.startsWith(tok)) {
        items.push({ kind: "hint", label: h.label, insert: h.insert });
      }
    }
    if (!tok) {
      for (const h of (history || []).slice(0, 5)) {
        if (h?.q) items.push({ kind: "recent", label: h.q, insert: h.q, replaceAll: true });
      }
    }
    return items.slice(0, 50);
  }, [dbs, dynamicFields, currentToken, history, monthContext, dbContext]);

  useEffect(() => {
    setHighlight(0);
  }, [suggestions.length, currentToken]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const inp = inputRef.current;
      const pnl = panelRef.current;
      if (inp && inp.contains(e.target)) return;
      if (pnl && pnl.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, inputRef]);

  const insertAtCursor = (text) => {
    if (!text) return;
    const cur = query || "";
    const pos = Math.min(cursor ?? cur.length, cur.length);
    const next = cur.slice(0, tokenStart) + text + cur.slice(pos);
    const newPos = tokenStart + text.length;
    setQuery(next);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(newPos, newPos);
        setCursor(newPos);
      }
    });
  };

  const applySuggestion = (sug) => {
    if (!sug) return;
    if (sug.replaceAll) {
      setQuery(sug.insert);
      setOpen(false);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(sug.insert.length, sug.insert.length);
          setCursor(sug.insert.length);
        }
      });
      return;
    }
    const text = query || "";
    const pos = Math.min(cursor ?? text.length, text.length);
    const next = text.slice(0, tokenStart) + sug.insert + text.slice(pos);
    const newPos = tokenStart + sug.insert.length;
    setQuery(next);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(newPos, newPos);
        setCursor(newPos);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (open && suggestions.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        applySuggestion(suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }
    onKeyDown?.(e);
  };

  const updateCursor = () => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) setCursor(el.selectionStart ?? 0);
    });
  };

  const scopedDbs = useMemo(() => {
    const all = dbs || [];
    const text = (query || "").trim();
    if (!text || !all.length) return all;
    const names = all.map((d) => d?.dbName).filter(Boolean);
    if (!names.length) return all;
    const dbAlt = names
      .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");
    const m = text.match(new RegExp(`\\s+in\\s+(${dbAlt})\\s*$`, "i"));
    if (!m) return all;
    const target = m[1].toLowerCase();
    const filtered = all.filter(
      (d) => (d?.dbName || "").toLowerCase() === target
    );
    return filtered.length ? filtered : all;
  }, [dbs, query]);

  const dbCount = scopedDbs.length;
  const dbList = scopedDbs.map((d) => d?.dbName).filter(Boolean).join(", ");

  return (
    <>
      <input
        ref={inputRef}
        className={className}
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          updateCursor();
        }}
        onKeyUp={updateCursor}
        onClick={updateCursor}
        onFocus={() => {
          setOpen(true);
          ensureFields();
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <div ref={panelRef} className="qa-panel" role="listbox">
          {dbCount > 0 && (
            <div className="qa-dbs">
              <span className="qa-dbs-label">
                {t("autocomplete.dbHeader", { count: dbCount })}
              </span>
              <span className="qa-dbs-list">{dbList}</span>
            </div>
          )}
          {dateContext && (
            <div className="qa-datepicker">
              <span className="qa-datepicker-label">
                {t("autocomplete.pickDate")}
              </span>
              <input
                type="date"
                onChange={(e) => insertAtCursor(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {suggestions.length === 0 ? (
            <div className="qa-empty">{t("autocomplete.empty")}</div>
          ) : (
            <ul className="qa-list">
              {suggestions.map((s, i) => (
                <li
                  key={`${s.kind}-${s.label}-${i}`}
                  className={`qa-item ${i === highlight ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applySuggestion(s);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  role="option"
                  aria-selected={i === highlight}
                >
                  <span className={`qa-tag qa-tag-${s.kind}`}>
                    {t(`autocomplete.kind.${s.kind}`)}
                  </span>
                  <span className="qa-label">{s.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
